import React from 'react'
import {
  HistoryEvent,
  BuildingPermitDetail,
  TransactionDetail,
  HistoryListingDetail,
  ResizableDetailPanel,
  DetailPanelHeader,
  DetailPanelContent,
  Text,
  Box,
  isPermitEvent,
  isTransactionEvent,
  isRentalEvent,
  isSaleEvent,
} from '@popety-io/ui'

export { DetailWrapper, ContentScroller } from './HistoryEventDetail.styles'

interface HistoryEventDetailProps {
  isOpen: boolean
  event: HistoryEvent | null
  onClose: () => void
  translations?: any
  loadTransaction?: (transactionId: number | string) => Promise<any>
  loadListing?: (listingId: number) => Promise<any>
}

export const HistoryEventDetail: React.FC<HistoryEventDetailProps> = ({
  isOpen,
  event,
  onClose,
  translations,
  loadTransaction,
}) => {
  if (!event) return null

  // For permit events, BuildingPermitDetail has its own panel
  if (isPermitEvent(event)) {
    return (
      <BuildingPermitDetail
        isOpen={isOpen}
        permit={event}
        onClose={onClose}
        contained={true}
      />
    )
  }

  // For transaction events, TransactionDetail fetches full data by ID
  if (isTransactionEvent(event)) {
    return (
      <TransactionDetail
        isOpen={isOpen}
        onClose={onClose}
        transactionId={event.id || event.transaction_id}
        loadTransaction={loadTransaction}
        contained={true}
      />
    )
  }

  // For rental/sale events, use HistoryListingDetail which displays embedded data
  if (isRentalEvent(event) || isSaleEvent(event)) {
    const typeLabel =
      event.type === 'rental'
        ? translations?.rental?.title || 'Rental Listing'
        : translations?.sale?.title || 'Sale Listing'

    return (
      <ResizableDetailPanel
        isOpen={isOpen}
        onClose={onClose}
        storageKey="history_listing_detail_panel_width"
        defaultWidth="700px"
        minWidth={400}
        maxWidthPercentage={0.8}
        contained={true}
      >
        <DetailPanelHeader onClose={onClose}>
          <Text variant="caption" color="secondary">
            {typeLabel}
          </Text>
        </DetailPanelHeader>
        <DetailPanelContent>
          <Box padding="24px">
            <HistoryListingDetail event={event} translations={translations} />
          </Box>
        </DetailPanelContent>
      </ResizableDetailPanel>
    )
  }

  return null
}

export default HistoryEventDetail
